import React, { useState } from "react";
import {
  TextInput,
  Button,
  Group,
  Stack,
  Alert,
  Card,
  Title,
  Text,
  Switch,
  Divider,
  SimpleGrid,
  PasswordInput,
} from "@mantine/core";
import { IconAlertCircle, IconArrowLeft } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { getAdminApiEndpoint, setAdminCompanyCode } from "../../config/config";
import { adminHeaders } from "../../services/adminApi";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import PageContent from "../shared/PageContent/PageContent";

// Welcome survey config captured at create time. The keys match the ones the
// welcome survey settings API uses (/v1/welcome-survey/settings), which is
// company host scoped - so they ride along on the admin create call instead.
//
// The Twilio credentials are what the survey sends and receives on. They have
// no working default, so the module can't be turned on without them - every
// field here is required once the toggle is on.
const REQUIRED_WELCOME_SURVEY_FIELDS = [
  { key: "twilio_account_sid", label: "Twilio Account SID" },
  { key: "twilio_auth_token", label: "Twilio Auth Token", secret: true },
  { key: "twilio_api_key_sid", label: "Twilio API Key SID" },
  {
    key: "twilio_api_key_secret",
    label: "Twilio API Key Secret",
    secret: true,
  },
  {
    key: "twilio_messaging_service_sid_outbound",
    label: "Twilio Outbound Messaging Service SID",
  },
  { key: "welcome_survey_inbound_phone", label: "Inbound Phone" },
];

const EMPTY_WELCOME_SURVEY = Object.fromEntries(
  REQUIRED_WELCOME_SURVEY_FIELDS.map(({ key }) => [key, ""])
);

const EMPTY_ADMIN = { first_name: "", last_name: "", email: "" };

const AddCompanyPage = () => {
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();

  const [name, setName] = useState("");
  const [companyCode, setCompanyCode] = useState("");
  const [admin, setAdmin] = useState(EMPTY_ADMIN);
  const [generalSuite, setGeneralSuite] = useState(true);
  const [welcomeSurvey, setWelcomeSurvey] = useState(false);
  const [surveyDefaults, setSurveyDefaults] = useState(EMPTY_WELCOME_SURVEY);
  const [twilioDiscount, setTwilioDiscount] = useState(false);
  const [sendSignupEmail, setSendSignupEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [surveyErrors, setSurveyErrors] = useState({});

  const setAdminField = (field, value) =>
    setAdmin(prev => ({ ...prev, [field]: value }));

  // Typing into a flagged credential clears its message, so the section stops
  // showing an error the value no longer has.
  const setSurveyField = (field, value) => {
    setSurveyDefaults(prev => ({ ...prev, [field]: value }));
    setSurveyErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // The admin is optional, but a half filled one isn't - all three fields go
  // together or none of them do.
  const adminFilled = Object.values(admin).filter(v => v.trim()).length;
  const wantsAdmin = adminFilled > 0;

  const validate = () => {
    if (!name.trim()) return "Company name is required";
    if (!companyCode.trim()) return "Company code is required";
    if (wantsAdmin) {
      if (!admin.first_name.trim()) return "Admin first name is required";
      if (!admin.last_name.trim()) return "Admin last name is required";
      if (!admin.email.trim()) return "Admin email is required";
      if (!admin.email.includes("@"))
        return "Please enter a valid admin email address";
    }
    if (!generalSuite && !welcomeSurvey)
      return "Select at least one module for the company";
    return null;
  };

  // The welcome survey credentials report on the fields themselves rather than
  // in the summary alert, so every missing one is flagged at once.
  const collectSurveyErrors = () => {
    if (!welcomeSurvey) return {};
    return REQUIRED_WELCOME_SURVEY_FIELDS.reduce((acc, { key, label }) => {
      if (!surveyDefaults[key].trim()) acc[key] = `${label} is required`;
      return acc;
    }, {});
  };

  // Turning a module off drops its section from the body, so nothing typed in
  // beforehand leaks into the created company.
  const buildPayload = () => {
    const payload = {
      name: name.trim(),
      code: companyCode.trim(),
      general_suite_enabled: generalSuite,
      welcome_survey_enabled: welcomeSurvey,
      send_signup_email: sendSignupEmail,
    };

    // The welcome_survey block is required exactly when the module is on, so it
    // goes in alongside the flag rather than being sent empty.
    if (welcomeSurvey) {
      payload.welcome_survey = {
        ...Object.fromEntries(
          Object.entries(surveyDefaults).map(([key, value]) => [
            key,
            value.trim(),
          ])
        ),
        welcome_survey_twilio_discount: twilioDiscount,
      };
    }

    return payload;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    const missingSurveyFields = collectSurveyErrors();

    setError(validationError || "");
    setSurveyErrors(missingSurveyFields);

    if (validationError || Object.keys(missingSurveyFields).length) {
      return;
    }

    setLoading(true);

    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(getAdminApiEndpoint("/v2/admin/client"), {
        method: "POST",
        headers: adminHeaders(jwt, ""),
        body: JSON.stringify(buildPayload()),
      });

      if (!response.ok) {
        throw new Error(`Failed to create company: ${response.status}`);
      }

      const result = await response.json();
      const company = result.data || result;
      const code = company?.code || companyCode.trim();

      // The company exists from here on - an admin that fails to attach is
      // reported on its own rather than losing the company that was created.
      // The endpoint takes the company from the X-Company-Code header, so the
      // body is only the person being added.
      if (wantsAdmin) {
        const adminResponse = await fetch(
          getAdminApiEndpoint("/v2/admin/company-admin"),
          {
            method: "POST",
            headers: adminHeaders(jwt, code),
            body: JSON.stringify({
              first_name: admin.first_name.trim(),
              last_name: admin.last_name.trim(),
              email: admin.email.trim(),
            }),
          }
        );

        if (!adminResponse.ok) {
          throw new Error(
            `Company created, but adding the administrator failed: ${adminResponse.status}`
          );
        }
      }

      showSuccess("Company created successfully");

      setAdminCompanyCode(code);
      navigate(`/client/${code}`);
    } catch (err) {
      setError(err.message || "Failed to create company");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContent
      breadcrumbs={[
        { label: "Clients", path: "/reppi-admin" },
        { label: "New Company" },
      ]}
      title="Add New Company"
      subtitle="Create a company, its first administrator and the modules it can access"
    >
      <Group mb="lg">
        <Button
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/reppi-admin")}
          variant="outline"
          disabled={loading}
        >
          Back to Clients
        </Button>
      </Group>

      <Stack gap="lg">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        {/* Company */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={3} mb="md">
            Company
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label="Company Name"
              placeholder="Enter company name"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
            <TextInput
              label="Company Code"
              placeholder="Enter company code"
              description="Identifies the company in admin URLs and the X-Company-Code header."
              value={companyCode}
              onChange={e => setCompanyCode(e.target.value)}
              required
            />
          </SimpleGrid>
          <Switch
            mt="lg"
            label="Send Signup Email"
            description="Emails the company's administrators their signup link as soon as the company is created."
            checked={sendSignupEmail}
            onChange={e => setSendSignupEmail(e.currentTarget.checked)}
          />
        </Card>

        {/* Company admin */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={3}>Company Admin</Title>
          <Text size="sm" c="dimmed" mb="md">
            Optional - leave blank to add administrators later from the client
            page.
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label="First Name"
              placeholder="Enter first name"
              value={admin.first_name}
              onChange={e => setAdminField("first_name", e.target.value)}
              required={wantsAdmin}
            />
            <TextInput
              label="Last Name"
              placeholder="Enter last name"
              value={admin.last_name}
              onChange={e => setAdminField("last_name", e.target.value)}
              required={wantsAdmin}
            />
          </SimpleGrid>
          <TextInput
            mt="md"
            label="Email"
            placeholder="Enter email address"
            type="email"
            value={admin.email}
            onChange={e => setAdminField("email", e.target.value)}
            required={wantsAdmin}
          />
        </Card>

        {/* Modules */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={3} mb="md">
            Modules
          </Title>

          <Switch
            label="General Suite"
            description="Unlocks training, performance, reps and every other authenticated page."
            checked={generalSuite}
            onChange={e => setGeneralSuite(e.currentTarget.checked)}
          />

          {generalSuite && (
            <Card
              mt="md"
              padding="md"
              radius="sm"
              withBorder
              bg="var(--mantine-color-gray-0)"
            >
              <Text fw={600} size="sm" mb="xs">
                General Suite Defaults
              </Text>
              <Text size="sm" c="dimmed">
                No defaults to configure at create time yet - the company starts
                on the standard suite settings.
              </Text>
            </Card>
          )}

          <Divider my="lg" />

          <Switch
            label="Welcome Survey"
            description="Unlocks the Welcome Survey page and its settings for company admins."
            checked={welcomeSurvey}
            onChange={e => {
              setWelcomeSurvey(e.currentTarget.checked);
              // Don't bring stale red back with the section on a re-toggle.
              setSurveyErrors({});
            }}
          />

          {welcomeSurvey && (
            <Card
              mt="md"
              padding="md"
              radius="sm"
              withBorder
              bg="var(--mantine-color-gray-0)"
            >
              <Text fw={600} size="sm" mb="xs">
                Welcome Survey Setup
              </Text>
              <Text size="sm" c="dimmed" mb="md">
                The survey sends and receives over the company's own Twilio
                account, so these are needed before the module can be turned on.
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {REQUIRED_WELCOME_SURVEY_FIELDS.map(
                  ({ key, label, secret }) => {
                    const Field = secret ? PasswordInput : TextInput;
                    return (
                      <Field
                        key={key}
                        label={label}
                        placeholder={`Enter ${label}`}
                        value={surveyDefaults[key]}
                        onChange={e => setSurveyField(key, e.target.value)}
                        error={surveyErrors[key]}
                        required
                      />
                    );
                  }
                )}
              </SimpleGrid>
              <Switch
                mt="lg"
                label="Twilio Discount"
                description="Bills the company at the discounted Twilio rate."
                checked={twilioDiscount}
                onChange={e => setTwilioDiscount(e.currentTarget.checked)}
              />
            </Card>
          )}
        </Card>

        <Group justify="flex-end" gap="sm">
          <Button
            variant="outline"
            onClick={() => navigate("/reppi-admin")}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={loading}
            disabled={!name.trim()}
          >
            Create Company
          </Button>
        </Group>
      </Stack>
    </PageContent>
  );
};

export default AddCompanyPage;
